import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import DocumentUploadModal from "../DocumentUploadModal";
import DocumentViewModal from "../../../../Components/DocumentViewModal";
import NoDataCreated from "../../../../Components/NoDataCreated";
import ErrorPage from "../../../../Components/ErrorPage";
import PageLoader from "../../../../Components/PageLoader";
import { goToTop } from "../../../../Components/goToTop";

import { checkFileOthers, checkText } from "../../../../Utils/Validator";
import { subFolderDocumentCreateAPI, subFolderDocumentDeleteAPI, subFolderDocumentGetDataAPI, subFolderDocumentUpdateAPI } from "../../../../Api/Project/SubFolderDocument";
import { SubFolderDocument } from "../../../../Controllers/Types/Project/ProjectInterface";
import TableActionLoader from "../../../../Components/TableActionLoader";

const SubFolder: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const projectData = location.state?.data;
    const subFolderId = projectData.subFolderId;

    const toast = useRef<Toast>(null);
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // const isShared: boolean = projectData.sharedBy !== null ? true : false;
    const isShared: boolean = projectData.can_edit === false ? true : false;
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);

    const [subFolderDocumentList, setSubFolderDocumentList] = useState<SubFolderDocument[]>([]);
    const [showDocumentViewModal, setShowDocumentViewModal] = useState(false);
    const [documentPath, setDocumentPath] = useState<string>('');
    const [selectedDocument, setSelectedDocument] = useState<SubFolderDocument | null>(null);

    /* ****************************** For Document upload ****************************** */
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [documentName, setDocumentName] = useState<string>('');
    const [uploadedFileName, setUploadedFileName] = useState<string>('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    /* error massage */
    const [errorDocumentName, setErrorDocumentName] = useState<string>('');
    const [errorUploadedFile, setErrorUploadedFile] = useState<string>('');

    const [documentUpdateHasChanges, setDocumentUpdateHasChanges] = useState<boolean>(false);
    const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

    const [allDocumentPath, setAllDocumentPath] = useState<string[]>([]);

    const handleOpenUploadModal = () => {
        setShowUploadModal(true);
        setDataState('Add');
    };

    const handleOpenEditModal = (document: SubFolderDocument) => {
        setDataState('Edit');
        setShowUploadModal(true);

        setSelectedDocument(document);
        setDocumentName(document?.title);
    };

    const handleClearFiles = () => {
        setUploadedFileName('');
        setUploadedFile(null);
    };

    const handleClearFields = () => {
        setDocumentName('');
        setUploadedFileName("");
        setUploadedFile(null);

        setSelectedDocument(null);
    };

    const handleClearErrors = () => {
        setErrorDocumentName("");
        setErrorUploadedFile("");
    };

    const handleCloseModal = () => {
        setShowUploadModal(false);
        handleClearFields();
        handleClearErrors();
    };

    const handleFileUpload = (file: File) => {
        setUploadedFileName(file.name);
        setUploadedFile(file);
    };

    /* ************************************************************************************************************************************************************ */
    useEffect(() => {
        subFolderDocumentDataGet();
    }, []);

    useEffect(() => {
        const allpath = subFolderDocumentList.map((e) => e.path);
        setAllDocumentPath(allpath);
    }, [subFolderDocumentList]);


    /* *********************************************** get sub folder document data list *********************************************************************** */
    const subFolderDocumentDataGet = async () => {
        if (subFolderId) {
            setDataLoading(true);
            const data = await subFolderDocumentGetDataAPI(Number(subFolderId));
            setDataLoading(false);
            setInitialDataLoading(false);
            if (data.status) {
                setSubFolderDocumentList(data.data || []);
            } else {
                setErrorMessage('An error occurred while retrieving document data.');
            }
        }
    }
    /* *********************************************** get sub folder document data list *********************************************************************** */
    /* ************************************************************************************************************************************************************ */


    /* ***************************************************************** Create document - start ********************************************************** */
    useEffect(() => {
        checkText(documentName, false, setErrorDocumentName);
    }, [documentName])

    useEffect(() => {
        checkFileOthers(uploadedFile!, false, setErrorUploadedFile);
    }, [uploadedFile])

    function checkCredentials(fileRequired: boolean): boolean {
        const isDocumentNameValid = checkText(documentName, true, setErrorDocumentName);
        const isUploadedFileValid = checkFileOthers(uploadedFile!, fileRequired, setErrorUploadedFile);

        return isDocumentNameValid && isUploadedFileValid;
    }

    const handleCreateDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(true)) {
            setLoading(true);
            const data = await subFolderDocumentCreateAPI(Number(subFolderId), documentName, uploadedFile!);
            setLoading(false);

            if (data.statusCode === 200) {
                subFolderDocumentDataGet();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document added successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error uploading document.',
                        detail: 'There was an error uploading document.',
                        life: 3000
                    });
                }
            }
        }
    };
    /* ***************************************************************** Create document - end ********************************************************** */

    useEffect(() => {
        if (selectedDocument) {
            if (documentName !== selectedDocument?.title) {
                setDocumentUpdateHasChanges(true);
            } else if (uploadedFile !== null) {
                setDocumentUpdateHasChanges(true);
            } else {
                setDocumentUpdateHasChanges(false);
            }
        }
    }, [selectedDocument, documentName, uploadedFile]);

    /* ***************************************************************** Update document - start ********************************************************** */
    const handleUpdateDocument = async (e: React.FormEvent) => {
        e.preventDefault();

        if (checkCredentials(false)) {
            setLoading(true);
            const data = await subFolderDocumentUpdateAPI(Number(selectedDocument?.id), documentName, uploadedFile!);
            setLoading(false);

            if (data.statusCode === 200) {
                subFolderDocumentDataGet();
                handleCloseModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document updated successfully.`,
                        life: 3000,
                    });
                }
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error uploading document.',
                        detail: 'There was an error uploading document.',
                        life: 3000
                    });
                }
            }
        } else {
        }
    };
    /* ***************************************************************** Update document - end ********************************************************** */

    /* ***************************************************************** Delete document - start ********************************************************** */
    const handleDeletedocumentData = (documentId: number): void => {
        confirmDialog({
            message: 'Are you sure you want to delete document detail?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteDocumentData(documentId),
            resizable: false,
            draggable: false,
            dismissableMask: true,
        });
    }

    // delete operation
    const deleteDocumentData = async (documentId: number) => {
        setdeleteLoadingId(documentId);
        const data = await subFolderDocumentDeleteAPI(documentId);

        setdeleteLoadingId(null);
        if (data.statusCode === 200) {
            subFolderDocumentDataGet();
            handleCloseModal();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Document deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error deleting document.',
                    detail: 'There was an error deleting document.',
                    life: 3000
                });
            }
        }
    }
    /* ***************************************************************** Delete document - end ********************************************************** */


    /* ***************************** For show ducument data **************************** */

    /* Table structure */
    const editDataBody = (rowData: SubFolderDocument) => {
        const document = rowData;

        return (
            <button className="data_action_btn primary_table_btn p-ripple"
                onClick={() => handleOpenEditModal(document)}>
                <i className="bi bi-pencil-square"></i>
                <Ripple />
            </button>
        )
    }

    const deleteDataBody = (rowData: SubFolderDocument) => {
        const documentId = rowData.id;
        return (
            <React.Fragment>
                {deleteLoadingId === documentId ? (
                    <TableActionLoader />
                ) : (
                    <button className="data_action_btn danger_table_btn p-ripple"
                        onClick={() => handleDeletedocumentData(documentId)}>
                        <i className="bi bi-trash3"></i>
                        <Ripple />
                    </button>
                )}
            </React.Fragment>
        )
    }

    const viewDocumentBody = (rowData: SubFolderDocument) => {
        const documentPath = rowData.path;
        return (
            <div className="tabel_btn_area justify-content-end justify-content-lg-center mt-1 mb-1">
                <Button
                    label="View"
                    className="tabel_btn view_btn"
                    onClick={() => handleOpenDocumentViewModal(documentPath)}
                />
            </div>
        )
    };
    /* ********************************************************************************* */


    /* ******************************* For view document ******************************* */
    const handleOpenDocumentViewModal = (path: string) => {
        setDocumentPath(path);
        setShowDocumentViewModal(true);
    };

    const handleCloseDocumentViewModal = () => {
        setShowDocumentViewModal(false);
    };
    /* ********************************************************************************* */

    const goBack = () => {
        navigate(-1);
        goToTop();
    };

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div>
                        <div className="page_content mt-0">
                            <div className="page_breadcrumb_area">
                                <ul className="page_breadcrumb">
                                    <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                        {projectData.job_number} : {projectData.address}
                                    </li>
                                    <li className="page_breadcrumb_item has_child">
                                        {projectData.folder}
                                    </li>
                                    <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                        {projectData.service_name}
                                    </li>
                                    <li className="page_breadcrumb_item active">
                                        {projectData.sub_folder_name}
                                    </li>
                                </ul>

                                <button className="back_btn p-ripple" onClick={goBack}>
                                    <i className="bi bi-arrow-left me-2"></i>
                                    Back
                                    <Ripple />
                                </button>
                            </div>
                            <div className="page_header_area mt-sm-2 justify-content-end">
                                <div className="page_action_area">
                                    {!isShared && (
                                        <button
                                            className="add_data_link_btn p-ripple"
                                            onClick={handleOpenUploadModal}
                                        >
                                            <i className="bi bi-arrow-up-circle"></i>
                                            <span>Add document</span>
                                            <Ripple />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="page_main_content_section">
                                {subFolderDocumentList?.length > 0 ? (
                                    <DataTable
                                        value={subFolderDocumentList}
                                        paginator
                                        size="small"
                                        rows={10}
                                        rowsPerPageOptions={[5, 10, 25, 50]}
                                        tableStyle={{ minWidth: '50rem' }}
                                        paginatorDropdownAppendTo={'self'}
                                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                        currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                        className="page_table p-0 p-sm-2"
                                        rowHover
                                        loading={dataLoading}
                                        scrollable
                                        dataKey="document_id"
                                    >
                                        <Column
                                            header="Document name"
                                            headerClassName="text_purple"
                                            field="title"
                                            body={(rowData: SubFolderDocument) => (
                                                <span>
                                                    {rowData?.title ? rowData?.title : '---'}
                                                </span>
                                            )}
                                            style={{ width: '30%' }}
                                        ></Column>

                                        <Column
                                            header="Upload date"
                                            headerClassName="text_purple"
                                            field="update_at"
                                            body={(rowData: SubFolderDocument) => (
                                                <span>
                                                    {rowData?.update_at ? rowData?.update_at : '---'}
                                                </span>
                                            )}
                                            style={{ width: '20%' }}
                                        ></Column>

                                        {!isShared && [
                                            <Column
                                                header="Edit"
                                                headerClassName="text_purple"
                                                body={editDataBody}
                                                alignHeader="center"
                                                className="data_action_btn_area"
                                                style={{ width: '15%' }}
                                            ></Column>,

                                            <Column
                                                header="Delete"
                                                headerClassName="text_purple"
                                                body={deleteDataBody}
                                                alignHeader="center"
                                                className="data_action_btn_area"
                                                style={{ width: '15%' }}
                                            ></Column>,
                                        ]}

                                        <Column
                                            body={viewDocumentBody}
                                            className="table_btn_area"
                                            style={{ width: '20%' }}
                                            frozen
                                            alignFrozen="right"
                                        ></Column>
                                    </DataTable>
                                ) : (
                                    <NoDataCreated
                                        message={`No document added. ${!isShared ? 'To add the Document click on upload button' : ''}`}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Document upload modal */}
                <DocumentUploadModal
                    isOpen={showUploadModal}
                    dataState={dataState}
                    onClose={handleCloseModal}
                    serviceName={projectData.sub_folder_name}
                    toast={toast}
                    documentName={documentName}
                    setDocumentName={setDocumentName}
                    uploadedFileName={uploadedFileName}
                    handleFileUpload={handleFileUpload}
                    handleCreateDocument={handleCreateDocument}
                    handleUpdateDocument={handleUpdateDocument}
                    loading={loading}
                    documentNameError={errorDocumentName}
                    fileError={errorUploadedFile}
                    labelName="Document name"
                    headerName="document"
                    isFileNameRequired={true}
                    isFileRequired={dataState === 'Add' ? true : false}
                    handleClearFile={handleClearFiles}
                    documentUpdateHasChanges={documentUpdateHasChanges}
                />

                {/* Document view modal */}
                <DocumentViewModal
                    allFileUrls={allDocumentPath}
                    isOpen={showDocumentViewModal}
                    onClose={handleCloseDocumentViewModal}
                    fileUrl={documentPath}
                />
            </div>
        </React.Fragment>
    )
}

export default SubFolder;